import React, { useCallback, useMemo, useState } from 'react'
import { IconButton, Tooltip } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import {
  useGetAiGuardrails,
  useInsertAiGuardrail,
  useUpdateAiGuardrail,
  useDeleteAiGuardrail
} from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import { useListStyles } from './useListStyles'
import { GUARDRAIL_FIELDS } from './fieldSchemas'
import { useToast } from './ToastContext'
import RecordDrawer from './RecordDrawer'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'

function GuardrailsTab () {
  const classes = useListStyles()
  const { showToast, showError } = useToast()
  const { data, isLoading } = useGetAiGuardrails()
  const insertMutation = useInsertAiGuardrail()
  const updateMutation = useUpdateAiGuardrail()
  const deleteMutation = useDeleteAiGuardrail()

  const [searchText, setSearchText] = useState('')
  const [drawer, setDrawer] = useState({ open: false, record: null })
  const [confirm, setConfirm] = useState(null)

  const tableData = useMemo(() => {
    const guardrails = data?.guardrails || []
    return guardrails.map(g => ({
      ...g,
      enabled: !!g.enabled,
      hidden: !!g.hidden,
      lastModified: g.modifiedDate || g.createdDate || null,
      lastModifiedBy: g.modifiedBy || g.createdBy || null
    }))
  }, [data])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'guardrailId',
      accessor: 'guardrailId',
      Header: 'ID',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'name', accessor: 'name', Header: 'Name' },
    { id: 'version', accessor: 'version', Header: 'Version' },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: 'Enabled',
      Cell: ({ value }) => value
        ? <span className={classes.enabledBadge}>enabled</span>
        : <span className={classes.disabledBadge}>disabled</span>
    },
    {
      id: 'hidden',
      accessor: 'hidden',
      Header: 'Hidden',
      Cell: ({ value }) => value
        ? <span className={classes.warnBadge}>hidden</span>
        : <span className={classes.disabledBadge}>visible</span>
    },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          <Tooltip title='Edit guardrail' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setDrawer({ open: true, record: row.original })}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete guardrail' arrow>
            <IconButton
              size='small'
              className={classes.dangerBtn}
              onClick={() => setConfirm({ guardrailId: row.original.guardrailId })}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'name', desc: false }], [])

  const handleDelete = useCallback(async () => {
    if (!confirm) return
    try {
      await deleteMutation.mutateAsync({ guardrailId: confirm.guardrailId })
      showToast('Guardrail deleted')
    } catch (err) {
      showError(err, 'Delete failed')
    } finally {
      setConfirm(null)
    }
  }, [confirm, deleteMutation, showToast, showError])

  const isMutating = insertMutation.isLoading || updateMutation.isLoading
  const openNew = () => setDrawer({ open: true, record: null })

  return (
    <>
      <div className={classes.newBtnWrap}>
        <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Guardrail</SydButton>
      </div>

      {isLoading ? (
        <Loading />
      ) : tableData.length === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateMessage}>
            No AI guardrails yet. Create one to get started.
          </div>
          <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Guardrail</SydButton>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.recordCount}>
              {tableData.length} {tableData.length === 1 ? 'guardrail' : 'guardrails'}
            </span>
            <input
              className={classes.searchInput}
              placeholder='Search guardrails…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='AI Guardrails'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      <RecordDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, record: null })}
        title={drawer.record ? drawer.record.name : 'New Guardrail'}
        entityLabel='Guardrail'
        fields={GUARDRAIL_FIELDS}
        record={drawer.record}
        isMutating={isMutating}
        onInsert={(payload) => insertMutation.mutateAsync(payload)}
        onUpdate={(payload) => updateMutation.mutateAsync({ ...payload, guardrailId: drawer.record.guardrailId })}
      />

      <ConfirmDeleteDialog
        open={!!confirm}
        entityLabel='Guardrail'
        recordId={confirm?.guardrailId}
        isMutating={deleteMutation.isLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default GuardrailsTab
